import { Media, useCustomQuery } from '@gripp/shared-logic';
import { StyleSheet, View } from 'react-native';
import { DocumentData } from './types';
import { useTypedMediaItem } from './useTypedMediaItem';
import { VideoComponent } from './videoComponent';

/**
 * Fetches a video media record fresh by id (so status/processing reflect the
 * latest server state, not the possibly-stale snapshot embedded in a routine
 * config) and renders it with the shared VideoComponent player. Mirrors
 * GetImageMediaComponent. `children` is overlaid on top of the player (used for
 * the remove button in the routine media-instruction admin form).
 *
 * `isAlignLeft` mirrors GetImageMediaComponent: left-align in the admin form
 * (where the media sits next to the remove control), otherwise center so
 * execution-time video instructions line up with centered image instructions.
 */
export const GetVideoMediaComponent: React.FC<{
  media: Media;
  children?: React.ReactNode;
  displayWidth?: number;
  isAlignLeft?: boolean;
}> = (props) => {
  const { data } = useCustomQuery({
    cacheKey: ['getMedia', props.media.id],
    query: GET_MEDIA,
    variables: {
      getMediaId: props.media.id,
    },
    options: {
      gcTime: 10000,
      staleTime: 10000,
    },
  });
  const { typedMediaItem: video } = useTypedMediaItem<DocumentData>(
    data?.getMedia
  );

  if (!props?.media.id || !video || !data) return null;

  return (
    <View
      style={[
        styles.container,
        { alignSelf: props.isAlignLeft ? 'flex-start' : 'center' },
      ]}
    >
      <VideoComponent
        mediaItem={video}
        displayWidth={props.displayWidth ?? 220}
        displayMode="outlined"
      />
      {props?.children}
    </View>
  );
};

const GET_MEDIA = `
  query GetMedia($getMediaId: ID!) {
    getMedia(id: $getMediaId) {
      createdAt
      updatedAt
      id
      name
      status
      type
      updatedBy {
        id
        email
      }
      extension
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    marginBottom: 20,
  },
});
